import type { TagValue } from "./types";

// This module shows a date or datetime value as a short text relative to now,
// e.g. "30m ago", "yesterday", or "in 3 days", and as an exact title for a
// tooltip. The texts use the calendar of the user, so "yesterday" is the day
// before today, not 24 hours ago.

export type DateValue = Extract<TagValue, { type: "date" | "datetime" }>;

export function isDateValue(value: TagValue): value is DateValue {
  return value.type === "date" || value.type === "datetime";
}

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;
// After this many days, the text is the date, because "800 days ago" is hard
// to read.
const MAX_RELATIVE_DAYS = 365;

const FULL_DATE = new Intl.DateTimeFormat(undefined, { dateStyle: "full" });
const FULL_DATETIME = new Intl.DateTimeFormat(undefined, { dateStyle: "full", timeStyle: "short" });
const SHORT_DATE = new Intl.DateTimeFormat(undefined, { dateStyle: "medium" });

export function describeDate(value: DateValue, now: number): { text: string; title: string } {
  if (value.type === "date") {
    const day = parseLocalDate(value.value);
    if (!day) return { text: value.value, title: value.value };
    return { text: relativeDays(dayDiff(now, day), day), title: FULL_DATE.format(day) };
  }
  const at = new Date(value.value);
  if (Number.isNaN(at.getTime())) return { text: value.value, title: value.value };
  return { text: relativeInstant(now, at), title: FULL_DATETIME.format(at) };
}

function relativeInstant(now: number, at: Date): string {
  const ms = at.getTime() - now;
  const abs = Math.abs(ms);
  if (abs < MINUTE) return "just now";
  if (abs < HOUR) return relative(ms, `${Math.floor(abs / MINUTE)}m`);
  const days = dayDiff(now, at);
  if (days === 0) return relative(ms, `${Math.floor(abs / HOUR)}h`);
  return relativeDays(days, at);
}

// `days` is the number of calendar days from today to `date`. It is negative
// for the past.
function relativeDays(days: number, date: Date): string {
  if (days === 0) return "today";
  if (days === -1) return "yesterday";
  if (days === 1) return "tomorrow";
  if (Math.abs(days) > MAX_RELATIVE_DAYS) return SHORT_DATE.format(date);
  return relative(days, `${Math.abs(days)} days`);
}

function relative(signed: number, span: string): string {
  return signed < 0 ? `${span} ago` : `in ${span}`;
}

// This function counts the calendar days from the day of `now` to the day of
// `date`. The rounding removes the hour that a change of daylight saving time
// adds or removes.
function dayDiff(now: number, date: Date): number {
  return Math.round((startOfDay(date) - startOfDay(new Date(now))) / DAY);
}

function startOfDay(date: Date): number {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

// A date value ("2026-10-13") is a day in the local calendar, not a UTC instant.
// These two functions convert between the value and the local day.
export function parseLocalDate(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  return match ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])) : null;
}

export function localDate(day: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${day.getFullYear()}-${pad(day.getMonth() + 1)}-${pad(day.getDate())}`;
}
